import { LoadingAndErrorComponent } from '@/components/loading-and-error/loading-and-error';
import { BusinessIndicatorPipe } from '@/features/business-indicators/business-indicator.pipe';
import { type AnyCssColor, createDesignSystemPalette } from '@/shared/ui/theming';
import { UiSectionContentComponent } from '@/ui/section/section-content/ui-section-content';
import { UiSectionComponent } from '@/ui/section/ui-section';
import { isNotNullish, isNullish } from '@/utils/typing/is-nullish.predicate';
import { CommonModule, UpperCasePipe } from '@angular/common';
import { Component, computed, effect, inject, type OnChanges, resource, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { HighchartsChartComponent } from '../../../../components/highcharts-chart/highcharts-chart';
import { AbstractTailoredTemplateWidget } from '../../../../components/template-widgets/AbstractTailoredWidgetTemplate.class';
import type { BusinessIndicatorId } from '../../../../interfaces/BusinessIndicator.interface';
import { LocalNumberPipe, SignificantDigitPipe } from '../../../../pipes/localnumber.pipe';
import { DashboardingProvider } from '../../../../providers/dashboarding.provider';
import { HighchartsStaticProvider } from '../../../../providers/highcharts/highcharts.static.provider';

import { LoadingAndErrorResourceBindingDirective } from '@/components/loading-and-error/loading-and-error-resource-binding.directive';
import type { BusinessIntelligenceContextualPeriodCode } from '@/interfaces/BusinessIntelligence.interface';
import type { BusinessIndicatorForYearResponse } from '../../details/services/advizeo-core-business-indicators.service';
import { BusinessIndicatorsService } from '../../details/services/business-indicators.service';
import {
    type BandBoundaryId,
    type BandCode,
    type BusinessIndicatorGaugeConfig,
    type CustomBand,
    WidgetsBusinessIndicatorsService
} from '../widgets-business-indicators.service';
import type { BandBoundaryColor } from './../widgets-business-indicators.service';

interface HighchartBandBoundary {
    bandBoundaryId: BandBoundaryId;
    name?: string;
    code: BandCode;
    to: number;
    from: number;
    year?: number;
    color: AnyCssColor;
    thickness: string;
}

@Component({
    selector: 'template-widget-business-indicator-gauge',
    imports: [
        HighchartsChartComponent,
        UiSectionComponent,
        UiSectionContentComponent,
        BusinessIndicatorPipe,
        SignificantDigitPipe,
        CommonModule,
        UpperCasePipe,
        TranslatePipe,
        LocalNumberPipe,
        LoadingAndErrorComponent,
        LoadingAndErrorResourceBindingDirective
    ],
    templateUrl: './template-widget-business-indicator-gauge.component.html',
    styleUrl: './template-widget-business-indicator-gauge.component.scss'
})
export class TemplateWidgetBusinessIndicatorGaugeComponent
    extends AbstractTailoredTemplateWidget<BusinessIndicatorGaugeConfig>
    implements OnChanges
{
    private _highchartsStaticProvider = inject(HighchartsStaticProvider);
    private _businessIndicatorsService = inject(BusinessIndicatorsService);
    private _dashboardingProvider = inject(DashboardingProvider);
    private _widgetsBusinessIndicatorsService = inject(WidgetsBusinessIndicatorsService);

    private _localNumberPipe = new LocalNumberPipe();
    private _significantDigitPipe = new SignificantDigitPipe();

    chartOptions: Highcharts.Options | null = null;

    yearConfig = signal<BusinessIntelligenceContextualPeriodCode | number | null>(null);

    computedYear = computed(() => this._widgetsBusinessIndicatorsService.getComputedYear(this.yearConfig()));

    businessIndicatorId = signal<BusinessIndicatorId | null>(null);
    isEstimated = signal<boolean | null>(null);
    bandBoundary = signal<
        { isCustomBand: false; bandBoundaryColors: BandBoundaryColor[] } | { isCustomBand: true; bandBoundaryColors: CustomBand[] }
    >({ isCustomBand: false, bandBoundaryColors: [] });
    gaugeName = signal<string | null>(null);
    displayLegend = signal<boolean>(true);

    classificationNodeId = toSignal(this._dashboardingProvider.getSelectedNodeId$());

    bandBoundaries: HighchartBandBoundary[] = [];

    data = resource({
        params: computed(() => ({
            classificationNodeId: this.classificationNodeId(),
            businessIndicatorId: this.businessIndicatorId(),
            year: this.computedYear()
        })),
        loader: ({ params: { classificationNodeId, businessIndicatorId, year } }) =>
            isNotNullish(businessIndicatorId) && isNotNullish(classificationNodeId) && isNotNullish(year)
                ? firstValueFrom(
                      this._businessIndicatorsService.fetchBusinessIndicatorForYear$(businessIndicatorId, classificationNodeId, year)
                  )
                : Promise.resolve(undefined)
    });

    constructor() {
        super();
        effect(() => {
            const businessIndicatorForYear: BusinessIndicatorForYearResponse | undefined = this.data.value();
            const isEstimated = this.isEstimated();
            const year = this.computedYear();
            const bandBoundary = this.bandBoundary();

            if (bandBoundary.isCustomBand) {
                const bandBoundaryColors: BandBoundaryColor[] = [];
                let i = 0;
                for (let bound of bandBoundary.bandBoundaryColors) {
                    bandBoundaryColors.push({
                        bandBoundaryId: `custom${i}` as BandBoundaryId,
                        code: 'custom',
                        color: bound.color
                    });
                    i++;
                }

                this.generateHighchartConfig(
                    businessIndicatorForYear ?? null,
                    isEstimated,
                    year,
                    bandBoundaryColors,
                    bandBoundary.bandBoundaryColors
                );
            } else if (bandBoundary.isCustomBand === false) {
                this.generateHighchartConfig(businessIndicatorForYear ?? null, isEstimated, year, bandBoundary.bandBoundaryColors, null);
            }
        });
    }

    ngOnChanges() {
        this.yearConfig.set(this.config.year);

        this.businessIndicatorId.set(this.config.businessIndicatorId);
        this.isEstimated.set(this.config.isEstimated);
        this.bandBoundary.set(this.config.bandBoundary);
        this.gaugeName.set(this.config.gaugeName);
        this.displayLegend.set(this.config.displayLegend);
    }

    generateHighchartConfig(
        businessIndicatorForYear: BusinessIndicatorForYearResponse | null,
        isEstimated: boolean | null,
        year: number | null,
        bandBoundaryColors: BandBoundaryColor[],
        customBands: CustomBand[] | null
    ) {
        if (businessIndicatorForYear && isNotNullish(isEstimated) && isNotNullish(year)) {
            const displayUnitLabel = businessIndicatorForYear.displayUnitCode;

            const valueFormatter = (value: number): string => `
                <div style="font-size: var(--theming-typography-size-21); color: var(--theming-color-palette-default-12-solid);">
                    ${this._localNumberPipe.transform(this._significantDigitPipe.transform(value))}
                </div>
                <div style="font-size: var(--theming-typography-size-7); color: var(--theming-color-palette-default-11-solid);">
                    ${displayUnitLabel}
                </div>`;

            const businessIndicatorValue = this._widgetsBusinessIndicatorsService.getBusinessIndicatorValue(
                businessIndicatorForYear.measured,
                businessIndicatorForYear.estimated,
                isEstimated
            );

            if (isNullish(businessIndicatorValue)) {
                this.chartOptions = null;
                return;
            }

            const minAndMaxAndBoundaries = isNotNullish(customBands)
                ? this._widgetsBusinessIndicatorsService.getBandBoundaryValuesFromCustom(customBands, businessIndicatorValue)
                : this._widgetsBusinessIndicatorsService.getBandBoundaryValues(businessIndicatorForYear, year, businessIndicatorValue);

            if (minAndMaxAndBoundaries) {
                const { min, max, bandBoundaryValues } = minAndMaxAndBoundaries;

                this.bandBoundaries = bandBoundaryValues.map(bandBoundaryValue => {
                    const bandBoundaryColor = bandBoundaryColors.find(
                        bandBoundColor => bandBoundColor.bandBoundaryId === bandBoundaryValue.bandBoundaryId
                    );
                    return bandBoundaryColor
                        ? {
                              ...bandBoundaryValue,
                              color: createDesignSystemPalette(bandBoundaryColor.color.paletteName)[bandBoundaryColor.color.variant].solid,
                              thickness: '40%'
                          }
                        : { ...bandBoundaryValue, color: '#fffff' as AnyCssColor, thickness: '40%' };
                });

                this.chartOptions = this._highchartsStaticProvider.buildConfig(
                    this._highchartsStaticProvider.DISABLE_ANIMATIONS,
                    this._highchartsStaticProvider.REFLOW_ON_CONTAINER_RESIZE,
                    {
                        chart: {
                            type: 'gauge',
                            plotBackgroundImage: null,
                            plotBorderWidth: 0,
                            plotShadow: false,
                            height: '100%',
                            spacing: [0, 0, 0, 0]
                        },
                        title: {
                            text: null
                        },
                        tooltip: {
                            enabled: false
                        },
                        pane: {
                            center: ['50%', '43%'],
                            startAngle: -90,
                            endAngle: 90,
                            background: null
                        },
                        yAxis: {
                            min,
                            max,
                            tickLength: 0,
                            tickWidth: 0,
                            minorTickInterval: null,
                            lineWidth: 0,
                            labels: {
                                enabled: false
                            },
                            plotBands: this.bandBoundaries
                        },
                        series: [
                            {
                                name: 'businessIndicatorValue',
                                data: [businessIndicatorValue],
                                dataLabels: [
                                    {
                                        formatter(this: Highcharts.Point) {
                                            return valueFormatter(this.y ?? 0);
                                        },
                                        borderWidth: 0,
                                        style: {
                                            textAlign: 'center',
                                            fontWeight: 'var(--theming-typography-medium)'
                                        },
                                        useHTML: true,
                                        y: 1
                                    }
                                ],
                                dial: {
                                    radius: '50%',
                                    backgroundColor: '#928b8a',
                                    baseWidth: 16,
                                    baseLength: '0%',
                                    rearLength: '0%'
                                },
                                pivot: {
                                    radius: 8,
                                    backgroundColor: '#928b8a'
                                }
                            }
                        ]
                    }
                );
            }
        }
    }
}
